// Funnely Premium Templates — Wix-quality, each visually DISTINCT
// Every template has unique: layout structure, font pairing, hero treatment, color personality

export const NICHE_CATEGORIES = [
  { id: 'all', label: 'All Templates', icon: 'grid' },
];

export const LAYOUT_TYPES = {
  'vivid': 'Vivid',
  'serene': 'Serene',
  'editorial': 'Editorial',
  'construct': 'Construct',
  'playful': 'Playful',
};

// High-quality stock photos
const IMG = {
  vivid_hero: 'https://images.unsplash.com/photo-1622151834677-70f982c9adef?w=1600&q=80',
  vivid_mid: 'https://images.unsplash.com/photo-1774509655380-dfc9ea34f604?w=800&q=80',
  serene_mid: 'https://images.pexels.com/photos/1926811/pexels-photo-1926811.jpeg?auto=compress&w=800',
  editorial_hero: 'https://images.unsplash.com/photo-1759925847124-dc276a15ff10?w=1600&q=80',
  construct_hero: 'https://images.unsplash.com/photo-1510519138101-570d1dca3d66?w=1600&q=80',
  playful_mid: 'https://images.unsplash.com/photo-1484980742600-93fbca072174?w=800&q=80',
};

// Section helpers
const hl = (content, font, fontSize, fontColor, align = 'center') => ({
  id: `hl_${Math.random().toString(36).slice(2,8)}`, type: 'headline', content, font, fontSize, fontColor, align,
});
const tx = (content, font, fontSize, fontColor, align = 'center') => ({
  id: `tx_${Math.random().toString(36).slice(2,8)}`, type: 'text', content, font, fontSize, fontColor, align,
});
const img = (url, size = 100) => ({
  id: `im_${Math.random().toString(36).slice(2,8)}`, type: 'image', url, size,
});
const divider = (color) => ({
  id: `dv_${Math.random().toString(36).slice(2,8)}`, type: 'divider', color,
});
const ctaBtn = (text, bgColor, textColor = '#FFFFFF', align = 'center') => ({
  id: `ct_${Math.random().toString(36).slice(2,8)}`, type: 'cta_button', text, url: '#checkout', bgColor, textColor, size: 'lg', align,
});
const featureGrid = (items, fontColor, accentColor) => ({
  id: `fg_${Math.random().toString(36).slice(2,8)}`, type: 'feature_grid', items, columns: 3, fontColor, accentColor,
});
const socialProof = (items, fontColor, accentColor) => ({
  id: `sp_${Math.random().toString(36).slice(2,8)}`, type: 'social_proof', items, fontColor, accentColor,
});
const guarantee = (title, description, fontColor, accentColor) => ({
  id: `gt_${Math.random().toString(36).slice(2,8)}`, type: 'guarantee', title, description, fontColor, accentColor,
});

export const PREMIUM_TEMPLATES = [

  // ═══════════════════════════════════════════════════════
  // 1. VIVID — Full-bleed dark hero photo, ALL CAPS bold, neon accent
  //    Inspired by: Roy Pratt (Wix), fitness/creator bold energy
  //    Font pair: Montserrat (headline) + Inter (body)
  //    Accent: Neon Yellow-Green #D4FF00
  //    Hero: Full-bleed photo with dark gradient overlay, left-aligned text
  //    Unique: Photo-driven, dark moody aesthetic, pill CTA, stats bar
  // ═══════════════════════════════════════════════════════
  {
    id: 'vivid',
    name: 'Vivid',
    niche: 'all',
    description: 'Bold, dark, photo-driven — for creators who want to make a statement',
    layout_type: 'vivid',
    preview: {
      gradient: 'linear-gradient(135deg, #0A0A0A, #1A1A1A)',
      accent: '#D4FF00',
      textLight: true,
      heroImage: IMG.vivid_hero,
    },
    data: {
      primary_color: '#D4FF00',
      background_color: '#0A0A0A',
      text_color: '#FAFAFA',
      accent_color: '#D4FF00',
      hero_gradient: '',
      hero_image: IMG.vivid_hero,
      headline: 'IT DOESN\'T GET\nEASIER. YOU GET\nBETTER.',
      headline_style: { font: 'Montserrat', fontSize: 64, fontColor: '#FFFFFF' },
      subheadline: 'Premium digital products crafted for ambitious creators who demand results.',
      subheadline_style: { font: 'Inter', fontSize: 17, fontColor: '#FFFFFFAA' },
      bio: '',
      custom_cta_text: 'GET STARTED',
      body_sections: [
        // Story / Problem section
        hl('THE PROBLEM', 'Montserrat', 12, '#D4FF00', 'left'),
        hl('You\'re tired of tools that\npromise everything and\ndeliver nothing.', 'Montserrat', 36, '#FAFAFA', 'left'),
        tx('Every other platform gives you a generic page builder and calls it a day. No strategy. No conversion thinking. Just drag, drop, and pray. You deserve better than that.', 'Inter', 16, '#FFFFFF88', 'left'),
        divider('#FFFFFF15'),
        // Solution section
        hl('THE SOLUTION', 'Montserrat', 12, '#D4FF00', 'left'),
        hl('Products that sell themselves.\nPages that actually convert.', 'Montserrat', 36, '#FAFAFA', 'left'),
        tx('Every template is built on proven conversion principles. Every section is placed with purpose. From headline to checkout — nothing is random.', 'Inter', 16, '#FFFFFF88', 'left'),
        img(IMG.vivid_mid, 100),
        divider('#FFFFFF15'),
        // Social proof
        socialProof([
          { value: '2,500+', label: 'Creators' },
          { value: '4.9/5', label: 'Rating' },
          { value: '50K+', label: 'Products Sold' },
        ], '#FAFAFA', '#D4FF00'),
        // Features
        hl('WHAT YOU GET', 'Montserrat', 12, '#D4FF00', 'center'),
        featureGrid([
          { icon: 'zap', title: 'Instant Access', desc: 'Download immediately after purchase' },
          { icon: 'shield', title: 'Secure Delivery', desc: 'Time-limited, encrypted download links' },
          { icon: 'star', title: 'Premium Quality', desc: 'Crafted by industry professionals' },
        ], '#FAFAFA', '#D4FF00'),
        divider('#FFFFFF15'),
        // CTA
        ctaBtn('GET INSTANT ACCESS', '#D4FF00', '#0A0A0A'),
        // Guarantee
        guarantee('100% MONEY-BACK GUARANTEE', 'Not satisfied? Full refund within 30 days. No questions asked. Zero risk.', '#FAFAFA', '#D4FF00'),
      ],
      show_branding: true,
    },
  },

  // ═══════════════════════════════════════════════════════
  // 2. SERENE — Clean white, serif elegance, calm wellness vibe
  //    Inspired by: G. Robinson (Wix), therapy/wellness
  //    Font pair: Playfair Display (headline) + Raleway (body)
  //    Accent: Sage Green #7C9070
  //    Hero: NO photo — clean white bg, massive centered serif headline, thin line
  //    Unique: Minimal, text-driven, horizontal feature cards, outlined CTA
  // ═══════════════════════════════════════════════════════
  {
    id: 'serene',
    name: 'Serene',
    niche: 'all',
    description: 'Clean, calm, elegant — for wellness, coaching, and premium services',
    layout_type: 'serene',
    preview: {
      gradient: 'linear-gradient(180deg, #FAFAF9, #F5F5F0)',
      accent: '#7C9070',
      textLight: false,
    },
    data: {
      primary_color: '#7C9070',
      background_color: '#FAFAF9',
      text_color: '#2D2D2D',
      accent_color: '#7C9070',
      hero_gradient: '',
      hero_image: '',
      headline: 'Find Your\nInner Peace.',
      headline_style: { font: 'Playfair Display', fontSize: 56, fontColor: '#2D2D2D' },
      subheadline: 'Guided programs and resources designed to bring balance, clarity, and calm to your everyday life.',
      subheadline_style: { font: 'Raleway', fontSize: 17, fontColor: '#6B6B6B' },
      bio: '',
      custom_cta_text: 'Begin Your Journey',
      body_sections: [
        img(IMG.serene_mid, 80),
        divider('#2D2D2D15'),
        hl('The Approach', 'Playfair Display', 32, '#2D2D2D', 'center'),
        tx('Every program is rooted in evidence-based practices. We combine mindfulness, movement, and reflection to create lasting transformation — not quick fixes.', 'Raleway', 16, '#6B6B6B', 'center'),
        divider('#2D2D2D15'),
        featureGrid([
          { icon: 'heart', title: 'Holistic Wellness', desc: 'Mind, body, and spirit — addressed together' },
          { icon: 'target', title: 'Guided Programs', desc: 'Step-by-step paths designed by experts' },
          { icon: 'shield', title: 'Safe Space', desc: 'Confidential, judgement-free support' },
        ], '#2D2D2D', '#7C9070'),
        divider('#2D2D2D15'),
        socialProof([
          { value: '1,200+', label: 'Clients Helped' },
          { value: '4.9', label: 'Rating' },
          { value: '8 Years', label: 'Experience' },
        ], '#2D2D2D', '#7C9070'),
        divider('#2D2D2D15'),
        ctaBtn('Begin Your Journey', '#7C9070', '#FFFFFF'),
        guarantee('Satisfaction Promise', 'If you don\'t feel a difference within 30 days, we\'ll refund your investment. No questions, no hassle.', '#2D2D2D', '#7C9070'),
      ],
      show_branding: true,
    },
  },

  // ═══════════════════════════════════════════════════════
  // 3. EDITORIAL — Split hero, massive serif, fashion/luxury magazine feel
  //    Font pair: DM Serif Display (headline) + DM Sans (body)
  //    Accent: Warm Coral #C9553D
  //    Hero: Split — HUGE serif title left (70%), vertical image right (30%)
  //    Unique: Magazine editorial, 2-col features, text-link CTA, double-line divider
  // ═══════════════════════════════════════════════════════
  {
    id: 'editorial',
    name: 'Editorial',
    niche: 'all',
    description: 'Magazine editorial — for fashion, luxury, design, and premium brands',
    layout_type: 'editorial',
    preview: {
      gradient: 'linear-gradient(180deg, #F5F0EB, #EDE8E3)',
      accent: '#C9553D',
      textLight: false,
      heroImage: IMG.editorial_hero,
    },
    data: {
      primary_color: '#C9553D',
      background_color: '#F5F0EB',
      text_color: '#2C1810',
      accent_color: '#C9553D',
      hero_gradient: '',
      hero_image: IMG.editorial_hero,
      headline: 'The Art of\nDigital Craft',
      headline_style: { font: 'DM Serif Display', fontSize: 52, fontColor: '#2C1810' },
      subheadline: 'A curated collection of premium digital products for the discerning creative.',
      subheadline_style: { font: 'DM Sans', fontSize: 16, fontColor: '#8B7355' },
      bio: '',
      custom_cta_text: 'Explore Collection',
      body_sections: [
        hl('Our Philosophy', 'DM Serif Display', 28, '#2C1810', 'center'),
        tx('We believe in quality over quantity. Every product is meticulously crafted, thoughtfully designed, and built to stand the test of time.', 'DM Sans', 16, '#8B7355', 'center'),
        divider('#2C181015'),
        img(IMG.editorial_hero, 90),
        divider('#2C181015'),
        featureGrid([
          { icon: 'award', title: 'Curated Selection', desc: 'Hand-picked by our editorial team' },
          { icon: 'star', title: 'Premium Quality', desc: 'Only the finest digital products' },
        ], '#2C1810', '#C9553D'),
        socialProof([
          { value: '850+', label: 'Products Sold' },
          { value: '4.9', label: 'Rating' },
          { value: 'Since 2020', label: 'Established' },
        ], '#2C1810', '#C9553D'),
        ctaBtn('Explore Collection', '#C9553D', '#FFFFFF'),
        guarantee('Quality Promise', 'Every product meets our editorial standards. Full refund if you disagree.', '#2C1810', '#C9553D'),
      ],
      show_branding: true,
    },
  },

  // ═══════════════════════════════════════════════════════
  // 4. CONSTRUCT — Full-bleed dark photo, RIGHT-aligned text, tech/business
  //    Font pair: Space Grotesk (headline) + Inter (body)
  //    Accent: Electric Blue #3B82F6
  //    Hero: Dark photo with RIGHT-aligned text (opposite of Vivid which is LEFT)
  //    Unique: 2x2 icon grid with blue border, compact stats bar, sharp CTA
  // ═══════════════════════════════════════════════════════
  {
    id: 'construct',
    name: 'Construct',
    niche: 'all',
    description: 'Professional tech — for business, SaaS, and consulting',
    layout_type: 'construct',
    preview: {
      gradient: 'linear-gradient(135deg, #0F172A, #1E293B)',
      accent: '#3B82F6',
      textLight: true,
      heroImage: IMG.construct_hero,
    },
    data: {
      primary_color: '#3B82F6',
      background_color: '#0F172A',
      text_color: '#F8FAFC',
      accent_color: '#3B82F6',
      hero_gradient: '',
      hero_image: IMG.construct_hero,
      headline: 'Build Something\nThat Matters.',
      headline_style: { font: 'Space Grotesk', fontSize: 48, fontColor: '#F8FAFC' },
      subheadline: 'Professional resources and tools designed for teams that ship fast and scale smart.',
      subheadline_style: { font: 'Inter', fontSize: 16, fontColor: '#94A3B8' },
      bio: '',
      custom_cta_text: 'Get the Toolkit',
      body_sections: [
        hl('WHY US', 'Space Grotesk', 12, '#3B82F6', 'left'),
        hl('Tools built for\nserious builders.', 'Space Grotesk', 32, '#F8FAFC', 'left'),
        tx('We don\'t do fluff. Every resource is battle-tested, production-ready, and designed to save you weeks of work.', 'Inter', 16, '#94A3B8', 'left'),
        divider('#F8FAFC10'),
        featureGrid([
          { icon: 'zap', title: 'Ship Faster', desc: 'Production-ready templates and tools' },
          { icon: 'shield', title: 'Enterprise Grade', desc: 'Security and reliability built in' },
          { icon: 'target', title: 'Results Focused', desc: 'Designed for measurable outcomes' },
          { icon: 'users', title: 'Team Ready', desc: 'Built for collaboration at scale' },
        ], '#F8FAFC', '#3B82F6'),
        socialProof([
          { value: '300+', label: 'Teams' },
          { value: '$2M+', label: 'Revenue Generated' },
          { value: '4.9', label: 'Rating' },
        ], '#F8FAFC', '#3B82F6'),
        divider('#F8FAFC10'),
        ctaBtn('Get the Toolkit', '#3B82F6', '#FFFFFF'),
        guarantee('60-Day Guarantee', 'If it doesn\'t deliver ROI, we\'ll refund every cent. No questions.', '#F8FAFC', '#3B82F6'),
      ],
      show_branding: true,
    },
  },

  // ═══════════════════════════════════════════════════════
  // 5. PLAYFUL — Colorful bg, rounded elements, warm fun vibe
  //    Font pair: Poppins (headline) + Work Sans (body)
  //    Accent: Vibrant Orange #F97316
  //    Hero: Warm yellow bg (NO photo), centered, rounded playful elements
  //    Unique: Rounded colorful cards, playful badge stats, bouncy CTA, wavy divider
  // ═══════════════════════════════════════════════════════
  {
    id: 'playful',
    name: 'Playful',
    niche: 'all',
    description: 'Fun, colorful, warm — for food, pets, lifestyle, and kids',
    layout_type: 'playful',
    preview: {
      gradient: 'linear-gradient(180deg, #FEF3C7, #FDE68A)',
      accent: '#F97316',
      textLight: false,
    },
    data: {
      primary_color: '#F97316',
      background_color: '#FEF3C7',
      text_color: '#1C1917',
      accent_color: '#F97316',
      hero_gradient: '',
      hero_image: '',
      headline: 'Good Things\nStart Here!',
      headline_style: { font: 'Poppins', fontSize: 48, fontColor: '#1C1917' },
      subheadline: 'Discover our collection of fun, creative digital products made with love and a whole lot of good vibes.',
      subheadline_style: { font: 'Work Sans', fontSize: 16, fontColor: '#78716C' },
      bio: '',
      custom_cta_text: 'Browse Products',
      body_sections: [
        img(IMG.playful_mid, 85),
        hl('Why You\'ll Love This', 'Poppins', 26, '#1C1917', 'center'),
        tx('Every product is crafted with care, tested thoroughly, and designed to make your life easier and more fun.', 'Work Sans', 16, '#78716C', 'center'),
        featureGrid([
          { icon: 'heart', title: 'Made with Love', desc: 'Every product is personally crafted' },
          { icon: 'zap', title: 'Instant Download', desc: 'Get your files immediately' },
          { icon: 'gift', title: 'Bonus Content', desc: 'Free extras with every purchase' },
        ], '#1C1917', '#F97316'),
        socialProof([
          { value: '2,000+', label: 'Happy Customers' },
          { value: '4.8', label: 'Stars' },
          { value: '100%', label: 'Love Rate' },
        ], '#1C1917', '#F97316'),
        ctaBtn('Browse Products', '#F97316', '#FFFFFF'),
        guarantee('Happiness Guarantee', 'Not smiling? Full refund within 30 days. We want you happy!', '#1C1917', '#F97316'),
      ],
      show_branding: true,
    },
  },

];
